import type * as RAPIER from '@dimforge/rapier3d-compat';

// Optional rigid-body debris via Rapier (WASM, ~2MB) — shards truly collide
// with each other and the ground, tumbling and piling where enemies died.
// Loaded lazily behind the "Debris physics" quality toggle; when unavailable
// (load failure, toggle off) ShapeShatter falls back to its hand-rolled
// integrator. Purely cosmetic: the core sim never knows this exists.

export interface ShardBody {
  handle: number;
}

export class DebrisPhysics {
  private constructor(
    private readonly rapier: typeof RAPIER,
    private readonly world: RAPIER.World,
  ) {}

  static async create(groundY: number): Promise<DebrisPhysics | null> {
    try {
      const rapier = await import('@dimforge/rapier3d-compat');
      await rapier.init();
      const world = new rapier.World({ x: 0, y: -12, z: 0 });
      // Ground slab sized to the arena; displacement is visual-only, so a flat
      // collider at groundY reads correctly.
      const groundBody = world.createRigidBody(rapier.RigidBodyDesc.fixed().setTranslation(0, groundY - 0.5, 0));
      world.createCollider(rapier.ColliderDesc.cuboid(60, 0.5, 60), groundBody);
      return new DebrisPhysics(rapier, world);
    } catch {
      return null;
    }
  }

  /** Register one shard as a convex-hull rigid body. Returns null when the
   *  hull fails (degenerate slivers) — caller falls back to the integrator. */
  addShard(
    localPositions: Float32Array,
    position: { x: number; y: number; z: number },
    velocity: { x: number; y: number; z: number },
    angular: { x: number; y: number; z: number },
  ): ShardBody | null {
    const bodyDesc = this.rapier.RigidBodyDesc.dynamic()
      .setTranslation(position.x, position.y, position.z)
      .setLinvel(velocity.x, velocity.y, velocity.z)
      .setAngvel({ x: angular.x, y: angular.y, z: angular.z })
      .setLinearDamping(0.05)
      .setAngularDamping(0.4);
    const body = this.world.createRigidBody(bodyDesc);
    const colliderDesc = this.rapier.ColliderDesc.convexHull(localPositions);
    if (!colliderDesc) {
      this.world.removeRigidBody(body);
      return null;
    }
    colliderDesc.setRestitution(0.3).setFriction(0.8).setDensity(1);
    this.world.createCollider(colliderDesc, body);
    return { handle: body.handle };
  }

  step(dt: number): void {
    // Rapier prefers a fixed timestep; clamp so tab-jank doesn't explode piles.
    this.world.timestep = Math.min(dt, 1 / 30);
    this.world.step();
  }

  /** Copy a body's transform onto a mesh-like target. False if it vanished. */
  readTransform(
    shard: ShardBody,
    outPosition: { set(x: number, y: number, z: number): unknown },
    outQuaternion: { set(x: number, y: number, z: number, w: number): unknown },
  ): boolean {
    const body = this.world.getRigidBody(shard.handle);
    if (!body) return false;
    const t = body.translation();
    const r = body.rotation();
    outPosition.set(t.x, t.y, t.z);
    outQuaternion.set(r.x, r.y, r.z, r.w);
    return true;
  }

  isResting(shard: ShardBody): boolean {
    const body = this.world.getRigidBody(shard.handle);
    return body ? body.isSleeping() : true;
  }

  remove(shard: ShardBody): void {
    const body = this.world.getRigidBody(shard.handle);
    if (body) this.world.removeRigidBody(body);
  }

  bodyCount(): number {
    // Minus the static ground body.
    return Math.max(0, this.world.bodies.len() - 1);
  }

  dispose(): void {
    this.world.free();
  }
}
